import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  TextField,
  MenuItem,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useMe } from '../api/hooks.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { api } from '../api/client.js';
import { KIND_COLOR, STATUS_OPTIONS, type ErrorKind } from './errorShared.js';
import { useT } from '../i18n/useT.js';

interface Occurrence {
  id: string;
  sessionId: string;
  projectId: string;
  user: string;
  url?: string;
  occurredAt: string;
}
interface ErrorDetail {
  id: string;
  projectId: string;
  projectName: string;
  kind: ErrorKind;
  status: string;
  message: string;
  sampleStack?: string;
  sampleUrl?: string;
  count: number;
  affectedSessions: number;
  firstSeenAt: string;
  lastSeenAt: string;
  occurrences: Occurrence[];
}

export function ErrorDetailPage() {
  const t = useT().signals.errors.detail;
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const base = `/v1/orgs/${orgId}/errors/${id}`;

  const group = useQuery({
    enabled: !!orgId && !!id,
    queryKey: ['error', orgId, id],
    queryFn: async () => (await api.get(base)).data as ErrorDetail,
  });

  const setStatus = useMutation({
    mutationFn: async (status: string) => (await api.patch(base, { status })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['error', orgId, id] });
      qc.invalidateQueries({ queryKey: ['errors', orgId] });
    },
  });

  const createTicket = useMutation({
    mutationFn: async (o: Occurrence) =>
      (
        await api.post(`/v1/orgs/${orgId}/tickets`, {
          title: g ? g.message.slice(0, 120) : 'Error',
          description: g?.sampleStack,
          priority: 'HIGH',
          projectId: o.projectId,
          sessionId: o.sessionId,
        })
      ).data as { id: string },
    onSuccess: (t) => navigate(`/tickets/${t.id}`),
  });

  if (group.isLoading) return <LinearProgress />;
  const g = group.data;
  if (!g) return <Typography>{t.notFound}</Typography>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/errors')}><ArrowBackIcon /></IconButton>
        <Chip size="small" label={g.kind} sx={{ bgcolor: `${KIND_COLOR[g.kind]}22`, color: KIND_COLOR[g.kind], fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          label={t.status}
          value={g.status}
          onChange={(e) => setStatus.mutate(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          {STATUS_OPTIONS.map((s) => (<MenuItem key={s} value={s}>{t.statuses[s] ?? s}</MenuItem>))}
        </TextField>
      </Stack>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'ui-monospace, monospace', mb: 1, wordBreak: 'break-word' }}>
          {g.message}
        </Typography>
        {g.sampleUrl && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-all' }}>{g.sampleUrl}</Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mt: 2 }}>
          <Metric label={t.events} value={g.count} />
          <Metric label={t.affectedSessions} value={g.affectedSessions} />
          <Metric label={t.firstSeen} value={formatDate(g.firstSeenAt)} />
          <Metric label={t.lastSeen} value={formatDate(g.lastSeenAt)} />
        </Box>
        {g.sampleStack && (
          <Box
            component="pre"
            sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', fontSize: 12, m: 0, whiteSpace: 'pre-wrap' }}
          >
            {g.sampleStack}
          </Box>
        )}
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2, pb: 1 }}>{t.recent}</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t.columns.when}</TableCell>
                <TableCell>{t.columns.user}</TableCell>
                <TableCell>{t.columns.url}</TableCell>
                <TableCell align="right">{t.columns.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {g.occurrences.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatDateTime(o.occurredAt)}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{o.user}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{o.url || '—'}</Typography></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button size="small" component={RouterLink} to={`/projects/${o.projectId}/sessions/${o.sessionId}`} startIcon={<PlayArrowIcon />}>
                        {t.watch}
                      </Button>
                      <Button size="small" onClick={() => createTicket.mutate(o)} disabled={createTicket.isPending} startIcon={<ConfirmationNumberIcon />}>
                        {t.ticket}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={700}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}
